// Inventory/Datagrid.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { IconButton, Tooltip, Typography } from '@mui/material';
import Popover from '@mui/material/Popover';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchInventory, deleteInventory } from '../../actions/inventory';
import { adjustStock } from '../../actions/inventory';
import CustomizedEditDialog from './EditInventory';
import AdjustStockDialog from './AdjustStock';
import DeleteConfirmDialog from './DeleteInventoryModal';
import AddBoxIcon from '@mui/icons-material/AddBox';
import TuneIcon from '@mui/icons-material/Tune';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HistoryIcon from '@mui/icons-material/History';
import StockHistoryDialog from './StockHistoryDialog';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

const EXPIRY_COLORS = {
  FAR_OK:   'transparent', 
  OK_6M: '#c1cd32',        // Expiring in 6 months
  OK_3M: '#ffda00',        // Expiring in 3 months
  OK_1M: '#ffba00',        // Expiring in 1 month
  SOON_1W: '#ff8904',      // Expiring in 1 week
  SOON_1D: '#ff3400c2',    // Expiring in 1 day
  EXPIRED: '#ff0000c2',    // Expired / today
  NEUTRAL: 'silver',
};



const getExpiryStatus = (dateLike) => {
  const d = dateLike ? new Date(dateLike) : null;
  if (!d || Number.isNaN(d.getTime())) {
    return { dot: EXPIRY_COLORS.NEUTRAL, label: '—' };
  }

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end   = new Date(d.getFullYear(),   d.getMonth(),   d.getDate());
  const diffDays = Math.round((end - start) / 86400000);

  if (diffDays < 0)  return { dot: EXPIRY_COLORS.EXPIRED, label: 'Expired' };
  if (diffDays === 0) return { dot: EXPIRY_COLORS.EXPIRED, label: 'Expires Today' };
  if (diffDays === 1) return { dot: EXPIRY_COLORS.SOON_1D, label: 'In 1 day' };
  if (diffDays <= 7)  return { dot: EXPIRY_COLORS.SOON_1W, label: `In ${diffDays} days` };
  if (diffDays <= 30) return { dot: EXPIRY_COLORS.OK_1M, label: `In ${diffDays} days` };
  if (diffDays <= 90) return { dot: EXPIRY_COLORS.OK_3M, label: `In ${diffDays} days` };
    if (diffDays <= 180) return { dot: EXPIRY_COLORS.OK_6M, label: `In ${diffDays} days` };

// 👉 more than 6 months away
return { dot: EXPIRY_COLORS.FAR_OK, label: `In ${diffDays} days` };
};

const STOCK_COLORS = {
  LOW:      'rgba(255, 200, 0, 0.2)',     // yellow
  CRITICAL: 'rgb(255 92 0 / 13%)',   // orange/red
  OUT:      'rgb(239 83 80 / 10%)',   // red
  NEUTRAL:  'transparent',
};

const EXPIRED_STOCK_RING = {
  border: '1.5px solid #9ca3af',          // silver ring
  bg: 'rgba(156,163,175,0.12)',            // subtle grey fill
  label: 'Expired but still in stock',
};


const getStockStatus = (row) => {
  const q = Number(row?.quantity ?? 0);
  const crit = row?.criticalThreshold;
  const low = row?.lowStockThreshold;

  if (q <= 0) {
    return { key: 'OUT', dot: STOCK_COLORS.OUT, bg: '1px solid rgb(239, 83, 80)', label: 'Out of stock' };
  }

  // Critical: 0 < q <= criticalThreshold
  if (crit != null && Number.isFinite(Number(crit)) && q <= Number(crit)) {
    return { key: 'CRITICAL', dot: STOCK_COLORS.CRITICAL, bg: '1px solid rgb(255, 92, 0)', label: 'Critical' };
  }

  // Low: criticalThreshold < q <= lowStockThreshold
  if (low != null && Number.isFinite(Number(low)) && q <= Number(low) && (crit == null || q > Number(crit))) {
    return { key: 'LOW', dot: STOCK_COLORS.LOW, bg: '1px solid #ffc800', label: 'Low' };
  }

  return { key: 'OK', dot: STOCK_COLORS.NEUTRAL, bg: '1px solid transparent', label: 'Good' };
};



const getDaysUntil = (dateLike) => {
  const d = dateLike ? new Date(dateLike) : null;
  if (!d || Number.isNaN(d.getTime())) return { text: '—', days: null, color: '#6b7280' };

  const now  = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end   = new Date(d.getFullYear(),   d.getMonth(),   d.getDate());

  const diffMs   = end - start;
  const diffDays = Math.round(diffMs / 86400000);

  if (diffDays === 0) return { text: 'expires today', days: 0, color: '#d97706' };
  if (diffDays > 0)   return { text: `in ${diffDays} day${diffDays>1?'s':''}`, days: diffDays, color: '#059669' };

  const past = Math.abs(diffDays);
  return { text: `${past} day${past>1?'s':''} ago`, days: diffDays, color: '#dc2626' };
};

// client-side stock filter helper
const applyStockFilter = (rows, stockLevelKey) => {
  if (!stockLevelKey || stockLevelKey === 'STOCK_ALL') return rows;

  return rows.filter((r) => {
    const q = Number(r.quantity ?? 0);
    const crit = r.criticalThreshold;
    const low = r.lowStockThreshold;

    if (stockLevelKey === 'OUT') return q === 0;

    if (stockLevelKey === 'CRIT') {
      return q > 0 && crit != null && Number.isFinite(Number(crit)) && q <= Number(crit);
    }

    if (stockLevelKey === 'LOW') {
      const isLow = q > 0 && low != null && Number.isFinite(Number(low)) && q <= Number(low);
      const isCrit = crit != null && Number.isFinite(Number(crit)) && q <= Number(crit);
      return isLow && !isCrit;
    }

    return true;
  });
};


// client-side type filter helper
const applyTypeFilter = (rows, typeKey) => {
  if (!typeKey || typeKey === 'TYPE_ALL') return rows;
  if (typeKey === 'TYPE_MED') return rows.filter((r) => r.itemType === 'MEDICINE');
  if (typeKey === 'TYPE_PROD') return rows.filter((r) => r.itemType === 'PRODUCT');
  return rows;
};

const CommodityGrid = ({ filter = {}, onRestockRow }) => {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.inventory.list || []);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow]   = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustRow, setAdjustRow] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRow, setConfirmRow]   = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [alertAnchorEl, setAlertAnchorEl] = useState(null);
const [alertRow, setAlertRow] = useState(null);

const handleAlertOpen = (event, row) => {
  event.stopPropagation();
  setAlertAnchorEl(event.currentTarget);
  setAlertRow(row);
};

const handleAlertClose = () => {
  setAlertAnchorEl(null);
  setAlertRow(null);
};

const alertOpen = Boolean(alertAnchorEl);


  // NEW: type filter state
  const [typeKey, setTypeKey] = useState('TYPE_ALL');

  const menuOpen = Boolean(menuAnchorEl);

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = (e) => {
    e?.stopPropagation?.();
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  // fetch medicines whenever expiry-related filter changes
  useEffect(() => {
    // backend will use expiry-related keys; stockLevel is ignored server-side
    dispatch(fetchInventory(filter || {}));
  }, [dispatch, filter]);

const rows = useMemo(
  () =>
    (items || [])
      .filter((i) => i && (i._id || i.id))
      .map((i) => {
        const raw = i.expiryDate ? new Date(i.expiryDate) : null;

        const item = i.itemId || {};
        const med = item.medicineId || null;
        const itemType = item.type;

        const isProduct = itemType === 'PRODUCT';

        const displayName = isProduct
          ? (item.name || item.productName || '—') // tweak if needed
          : (med?.brandName || med?.genericName || '—');

        const genericName = itemType === 'MEDICINE'
          ? (med?.genericName || '')
          : '';

        return {
          id: i._id,

          item: item,     
          itemId: item._id, 

          itemType,                // 'PRODUCT' or 'MEDICINE'

          medicineId: med?._id || null,  // optional, only for MEDICINE

          displayName,
          medicineName: displayName,     // ✅ backward-compat alias
          genericName,

          supplier: i.supplier,
          strength: i.strength,
          batchNumber: i.batchNo,
          expiryDate: raw ? raw.toLocaleDateString('en-GB') : '—',
          expiryDateRaw: raw,

          quantity: i.quantity,
          wholesalePrice: i?.wholesalePrice ?? null,
          retailPrice: i?.retailPrice ?? null,
          discount: i?.discount ?? 0,
          lowStockThreshold: i?.lowStockThreshold ?? null,
          criticalThreshold: i?.criticalThreshold ?? null,
        };
      }),
  [items]
);



  const stockLevelKey = filter?.stockLevel || 'STOCK_ALL';

  const finalRows = useMemo(() => {
    const stockFiltered = applyStockFilter(rows, stockLevelKey);
    return applyTypeFilter(stockFiltered, typeKey);
  }, [rows, stockLevelKey, typeKey]);

  const columns = useMemo(
    () => [
      {
  field: 'displayName',          // ✅ matches row prop now
  headerName: 'Item',
  flex: 1,
  minWidth: 260,
  headerAlign: 'left',
  align: 'left',
  renderCell: (params) => {
    const isProduct = params.row.itemType === 'PRODUCT';
    const TypeIcon = isProduct ? Inventory2OutlinedIcon : MedicationOutlinedIcon;
    const typeColor = isProduct ? 'rgb(11 157 0)' : 'rgb(0 90 188)';
    const typeLabel = isProduct ? 'Product' : 'Medicine';
    const typeBg    = isProduct
      ? 'linear-gradient(-45deg,rgba(34, 197, 94, 0.3),rgba(52, 211, 153, 0.3))'
      : 'linear-gradient(-45deg,rgba(82, 106, 244, 0.4),rgba(62, 200, 224, 0.4))';
    const typeFontSize = isProduct ? '20px' : '22px';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1.25, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          <Tooltip title={typeLabel}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
                background: typeBg,
                marginRight: '7.5px',
              }}
            >
              <TypeIcon sx={{ fontSize: typeFontSize, color: typeColor }} />
            </Box>
          </Tooltip>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              {params.row.displayName || '—'}      {/* 🔥 use displayName */}
            </Typography>

            <Tooltip title="View stock alert levels">
              <IconButton
                size="small"
                onClick={(e) => handleAlertOpen(e, params.row)}
                sx={{
                  p: 0.25,
                  color: '#6b7280',
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: 'grey',
            fontSize: 13,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {params.row.genericName || ''}
        </Typography>
      </Box>
    );
  },
},

      { field: 'supplier', headerName: 'Supplier', width: 150, headerAlign: 'left', align: 'left' },
      { field: 'strength', headerName: 'Strength', width: 100, headerAlign: 'left', align: 'left' },
      { field: 'batchNumber', headerName: 'Batch Number', width: 120, headerAlign: 'left', align: 'left' },
      {
        field: 'expiryDate',
        headerName: 'Expiry Date',
        width: 150,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => {
  const st = getExpiryStatus(params.row.expiryDateRaw);
  const info = getDaysUntil(params.row.expiryDateRaw);
  const isFarOk = st.dot === EXPIRY_COLORS.FAR_OK;

  const text =
    st.label === '—'
      ? '—'
      : info.days === 0
      ? 'Expires Today'
      : info.days > 0
      ? `In ${info.days} day${info.days > 1 ? 's' : ''}`
      : 'Expired';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, gap: 0.5 }}>
      {/* Dot + text (no chip) */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {!isFarOk && (
      <Box
        sx={{
          width: 15,
          height: 15,
          borderRadius: '50%',
          backgroundColor: st.dot,
          flex: '0 0 auto',
        }}
      />
    )}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
          {text}
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
        {params.row.expiryDate || '—'}
      </Typography>
    </Box>
  );
},
      },
      // { field: 'quantity', headerName: 'Quantity', width: 80, headerAlign: 'left', align: 'left' },
      {
  field: 'quantity',
  headerName: 'Quantity',
  width: 80,
  headerAlign: 'left',
  align: 'left',
  renderCell: (params) => {
  const row = params.row;
  const q = row.quantity ?? 0;

  const expiry = getExpiryStatus(row.expiryDateRaw);
  const stock  = getStockStatus(row);

  const isExpired =
    expiry.label === 'Expired' || expiry.label === 'Expires Today';

  const isStockBad =
    stock.key === 'LOW' || stock.key === 'CRITICAL' || stock.key === 'OUT';

  const showNormalStock = isStockBad && !isExpired;
  const showExpiredStock = isExpired && q > 0;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {showNormalStock && (
        <Tooltip title={stock.label}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: stock.bg,
              backgroundColor: stock.dot,
              flex: '0 0 auto',
            }}
          />
        </Tooltip>
      )}

      {showExpiredStock && (
        <Tooltip title={EXPIRED_STOCK_RING.label}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: EXPIRED_STOCK_RING.border,
              backgroundColor: EXPIRED_STOCK_RING.bg,
              flex: '0 0 auto',
            }}
          />
        </Tooltip>
      )}

      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
        {q}
      </Typography>
    </Box>
  );
},

},


      {
        field: 'wholesalePrice',
        headerName: 'Wholesale Price',
        width: 120,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ value }) => {
          const num = Number(value);
          return <span>{Number.isFinite(num) ? `Rs ${num.toFixed(2)}` : '—'}</span>;
        },
      },
      {
        field: 'retailPrice',
        headerName: 'Retail Price',
        width: 110,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ value }) => {
          const num = Number(value);
          return <span>{Number.isFinite(num) ? `Rs ${num.toFixed(2)}` : '—'}</span>;
        },
      },
      {
        field: 'discount',
        headerName: 'Disc %',
        width: 80,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 85,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Keep Restock visible */}
            <Tooltip title="Restock">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRestockRow) onRestockRow(params.row);
                }}
                sx={{ background: 'transparent', padding: '3px', borderRadius: '50%' }}
              >
                <AddBoxIcon fontSize="inherit" sx={{ color: '#1976d2' }} />
              </IconButton>
            </Tooltip>

            {/* Kebab menu */}
            <Tooltip title="More">
              <IconButton
                size="small"
                onClick={(e) => openMenu(e, params.row)}
                sx={{ background: 'transparent', padding: '6px', borderRadius: '50%' }}
              >
                <MoreVertIcon fontSize="inherit" sx={{ color: '#6b7280' }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [onRestockRow]
  );

  return (
    <Box sx={{ height: 600, width: '100%', position: 'relative' }}>
      {/* Inject type filter into the existing (built-in) toolbar row */}
      <Box
        id="inventory-type-toggle"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={typeKey}
          onChange={(e, v) => {
            if (v) setTypeKey(v);
          }}
          sx={{
            background: '#f3f4f6',
            borderRadius: '5px',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 700,
              border: 'none',
              px: 1.5,
              borderRadius: '5px',
            },
          }}
        >
          <ToggleButton value="TYPE_ALL">All</ToggleButton>
          <ToggleButton value="TYPE_MED" 
          sx={{
            '&.Mui-selected': {
        color: 'rgb(0, 90, 188) !important',
      },
          }}>
            <MedicationOutlinedIcon />
             Medicines
          </ToggleButton>
          <ToggleButton value="TYPE_PROD"
          sx={{
            '&.Mui-selected': {
        color: 'rgb(11, 157, 0) !important',
      },
          }}
          >
            <Inventory2OutlinedIcon />
            Products
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <DataGridPro
        rows={finalRows}
        columns={columns}
        showToolbar
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 300, placeholder: 'Search…' },
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
        }}
        // sx={{
        //   '& .MuiDataGrid-toolbar': {
        //     justifyContent: 'start !important',
        //     gap: 1,
        //     pl: 1,
        //     pr: 24, // space for the toggle on the right
        //     background: '#fafafa',
        //   },
        //   border: 'none',
        //   '& .MuiDataGrid-columnHeaders': {
        //     backgroundColor: '#fafafa',
        //     borderBottom: '1px solid #e5e7eb',
        //     fontWeight: 600,
        //     fontSize: '0.9rem',
        //     color: '#374151',
        //   },
        //   '& .MuiDataGrid-cell': {
        //     borderBottom: '1px solid #f3f4f6',
        //     display: 'flex',
        //     alignItems: 'center',
        //     fontSize: '0.9rem',
        //     color: '#111827',
        //     py: '15px',
        //   },
        //   '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
        //   '& .MuiDataGrid-cell:first-of-type': { fontWeight: 700 },
        //   '& .MuiDataGrid-cell': {borderRight: '2px solid #fafafa'},
        //   '& .MuiDataGrid-cell:last-of-type': {borderRight: 'none',},
        // }}

        sx = {{
          '& .MuiDataGrid-toolbar': {
    justifyContent: 'start !important',
    gap: 1,
    pl: 1,
    pr: 24,
    background: '#fafafa',
  },
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#fafafa',
    // borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#374151',
  },

  // ✅ single rule
  '& .MuiDataGrid-cell': {
    borderRight: '1px solid #f7f7f7',    // column separator
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    color: '#111827',
    py: '15px',
  },

  '& .MuiDataGrid-row:hover': { backgroundColor: '#f9fafb' },
  '& .MuiDataGrid-cell:first-of-type': { fontWeight: 700 },

  // ⚠️ this selector is unreliable in DataGrid (virtualized), better target data-field
  '& .MuiDataGrid-cell[data-field="actions"]': { borderRight: 'none' },
  '& .MuiDataGrid-columnHeader[data-field="actions"]': { borderRight: 'none' },
        }}
        pageSizeOptions={[5, 10, 20]}
        initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
        paginationMode="client"
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
      />

      <CustomizedEditDialog
        open={editOpen}
        row={editRow}
        onClose={() => setEditOpen(false)}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        row={confirmRow}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(row) => {
          dispatch(deleteInventory(row.id));
          setConfirmOpen(false);
        }}
      />

      <AdjustStockDialog
  open={adjustOpen}
  row={{
    id: adjustRow?._id ?? adjustRow?.id,        // important
    medicineName: adjustRow?.displayName,       // comes from displayName
    genericName: adjustRow?.genericName,
    batchNumber: adjustRow?.batchNo ?? adjustRow?.batchNumber,
    quantity: adjustRow?.quantity,
  }}
        onClose={() => setAdjustOpen(false)}
        onGoToRestock={() => {
          setAdjustOpen(false);
          if (onRestockRow && adjustRow) onRestockRow(adjustRow);
        }}
        onSubmit={async ({ inventoryId, mode, newQty, delta, reason, note }) => {
          await dispatch(
            adjustStock({ inventoryId, mode, newQty, delta, reason, note })
          );
          setAdjustOpen(false);
        }}
      />

      <StockHistoryDialog
        open={historyOpen}
        row={historyRow}
        onClose={() => setHistoryOpen(false)}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setEditRow(menuRow);
            setEditOpen(true);
            closeMenu(e);
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setAdjustRow(menuRow);
            setAdjustOpen(true);
            closeMenu(e);
          }}
        >
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Adjust Stock</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setHistoryRow(menuRow);
            setHistoryOpen(true);
            closeMenu(e);
          }}
        >
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Stock History</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setConfirmRow(menuRow);
            setConfirmOpen(true);
            closeMenu(e);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'red' }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ sx: { color: 'red', fontWeight: 600 } }}
          />
        </MenuItem>
      </Menu>
      <Popover
  open={alertOpen}
  anchorEl={alertAnchorEl}
  onClose={handleAlertClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
>
  <Box sx={{ p: 1.5, maxWidth: 260 }}>
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, mb: 0.5, color: '#111827' }}
    >
      Stock alerts for this batch
    </Typography>

    {(!alertRow?.lowStockThreshold && !alertRow?.criticalThreshold) ? (
      <Typography
        variant="body2"
        sx={{ fontSize: 13, color: '#4b5563' }}
      >
        No stock alerts set for this batch.
      </Typography>
    ) : (
      <>
        <Typography
          variant="body2"
          sx={{ fontSize: 13, color: '#4b5563' }}
        >
          Low stock alert:{' '}
          {alertRow?.lowStockThreshold != null
            ? `${alertRow.lowStockThreshold} unit${alertRow.lowStockThreshold === 1 ? '' : 's'}`
            : '—'}
        </Typography>

        <Typography
          variant="body2"
          sx={{ fontSize: 13, color: '#4b5563', mt: 0.5 }}
        >
          Critical alert:{' '}
          {alertRow?.criticalThreshold != null
            ? `${alertRow.criticalThreshold} unit${alertRow.criticalThreshold === 1 ? '' : 's'}`
            : '—'}
        </Typography>
      </>
    )}
  </Box>
</Popover>

    </Box>
  );
};

export default CommodityGrid;
